import React, {FunctionComponent, useEffect, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {
    AppBar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Tab,
    Tabs,
    Theme,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import StationFilterTab from "@/Domains/DispositionManagement/Components/DispositionControls/Elements/Filter/Elements/StationFilterTab";
import {DISPOSITION_SET_FILTER, FETCHING_DATA_START,} from "@/Redux/Disposition/Constants";
import GroupFilterTab from "@/Domains/DispositionManagement/Components/DispositionControls/Elements/Filter/Elements/GroupFilterTab";
import EquipmentFilterTab from "@/Domains/DispositionManagement/Components/DispositionControls/Elements/Filter/Elements/EquipmentFilterTab";
import {useAppDispatch, useAppSelector,} from "@/Redux/Redux";
import GeneralFilterTab from "@/Domains/DispositionManagement/Components/DispositionControls/Elements/Filter/Elements/GeneralFilterTab";
import {RootStateOrAny, useSelector} from "react-redux";
import {selectShowOnlyGroupResOfStation} from "@/Redux/Disposition/DispositionReducer";
import {selectViewerEnabledGroups, ViewerState,} from "@/Redux/Auth/AuthReducer";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

interface FilterModalProps {
    open: boolean;
    onClose: () => void;
    viewData: any;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
    viewData?: any;
}

const useStyles = makeStyles()((theme: Theme) => ({
    checkboxAllLabel: {
        fontWeight: 700,
    },

    appBar: {
        position: "relative",
        boxShadow: "none",
    },

    title: {
        flex: 1,
    },

    toolbar: {
        height: 94,
    },
}));

const FilterModal: FunctionComponent<FilterModalProps> = ({
                                                              open,
                                                              onClose,
                                                              viewData,
                                                              ...other
                                                          }): JSX.Element => {
    const {classes} = useStyles();
    const dispatch = useAppDispatch();
    const [value, setValue] = useState(0);
    const {t} = useTranslation();

    const {
        dispositionStartdate,
        dispositionEnddate,
        stationFilter,
        equipmentFilter,
        vehicleGroupFilter,
        regionFilter,
        categoryFilter,
    } = useAppSelector((state) => state.disposition);

    const [tmpStationFilter, setTmpStationFilter] = useState(stationFilter);
    const [tmpGroupFilter, setTmpGroupFilter] = useState(vehicleGroupFilter);
    const [tmpRegionFilter, setTmpRegionFilter] = useState(regionFilter);
    const [tmpEquipmentFilter, setTmpEquipmentFilter] =
        useState(equipmentFilter);
    const [tmpCategoryFilter, setTmpCategoryFilter] = useState(categoryFilter);
    const showOnlyResOfStation = useSelector((state) =>
        selectShowOnlyGroupResOfStation(state as RootStateOrAny),
    );
    const [
        tmpShowOnlyGroupResOfStationFilter,
        setTmpShowOnlyGroupResOfStationFilter,
    ] = useState(showOnlyResOfStation);
    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    const isVehicleLog = window.location.href.includes('manager/log');

    useEffect(() => {
        if (tmpStationFilter !== stationFilter) {
            setTmpStationFilter(stationFilter);
        }
        if (tmpGroupFilter !== vehicleGroupFilter) {
            setTmpGroupFilter(vehicleGroupFilter);
        }
        if (tmpRegionFilter !== regionFilter) {
            setTmpRegionFilter(regionFilter);
        }
        if (tmpEquipmentFilter !== equipmentFilter) {
            setTmpEquipmentFilter(equipmentFilter);
        }
        if (tmpCategoryFilter !== categoryFilter) {
            setTmpCategoryFilter(categoryFilter);
        }
    }, [
        stationFilter,
        vehicleGroupFilter,
        regionFilter,
        equipmentFilter,
        categoryFilter,
    ]);

    const groups = viewData?.vehicleGroupList?.vehicleGroups;

    const enabledGroups = useSelector((state) =>
        selectViewerEnabledGroups(state as ViewerState),
    );
    const availableCategories = viewData?.categoryList?.categories;
    const categoryInStorage = useAppSelector(
        (state) => state.disposition.categoryFilter[0],
    );
    const [selectedCategory, setSelectedCategory] = useState(
        categoryInStorage || "-1",
    );
    const getGroupsForFilter = (categoryToFilter: string) => {
        let allGroups = groups;

        const allGroupsEnabledForViewer: any = [];
        if (enabledGroups !== null) {
            for (const index in enabledGroups) {
                allGroupsEnabledForViewer.push(
                    enabledGroups[index].vehicleGroupId,
                );
            }

            allGroups = allGroups.filter(function (
                value: any,
                index: number,
                arr: any,
            ) {
                return (
                    allGroupsEnabledForViewer.indexOf(value.vehicleGroupId) > -1
                );
            });
        } else {
            for (const index in allGroups) {
                allGroupsEnabledForViewer.push(allGroups[index].vehicleGroupId);
            }
        }

        allGroups = allGroups.filter(function (
            value: any,
            index: number,
            arr: any,
        ) {
            return allGroupsEnabledForViewer.indexOf(value.vehicleGroupId) > -1;
        });

        const allGroupsForSelectedCategory: any = [];
        let categoryIndex = "-1";
        for (const index in availableCategories) {
            const category = availableCategories[index];
            if (category.categoryId === categoryToFilter) {
                categoryIndex = index;
            }
        }
        if (categoryIndex !== "-1") {
            const enabledGroupsInCategory =
                availableCategories[categoryIndex]?.enabledVehicleGroups || [];

            for (const index in enabledGroupsInCategory) {
                allGroupsForSelectedCategory.push(
                    enabledGroupsInCategory[index].vehicleGroupId,
                );
            }
        } else {
            for (const index in allGroups) {
                allGroupsForSelectedCategory.push(
                    allGroups[index].vehicleGroupId,
                );
            }
        }
        return allGroups.filter(function (value: any, index: number, arr: any) {
            return (
                allGroupsEnabledForViewer.indexOf(value.vehicleGroupId) > -1 &&
                allGroupsForSelectedCategory.indexOf(value.vehicleGroupId) > -1
            );
        });
    };
    const groupsForFilter = getGroupsForFilter(selectedCategory);
    const handleAcceptFilters = () => {
        dispatch({
            type: DISPOSITION_SET_FILTER,
            data: {
                stationFilter: tmpStationFilter,
                equipmentFilter: tmpEquipmentFilter,
                vehicleGroupFilter:
                    tmpGroupFilter.length > 0
                        ? tmpGroupFilter
                        : groupsForFilter.map((group: any) => {
                            return group.vehicleGroupId;
                        }),
                regionFilter: tmpRegionFilter,
                categoryFilter: tmpCategoryFilter,
                showOnlyGroupResOfStationFilter:
                tmpShowOnlyGroupResOfStationFilter,
            },
        });
        dispatch({
            type: FETCHING_DATA_START,
            payload: {
                dispositionStartdate,
                dispositionEnddate,
            },
        });
        onClose();
    };

    function a11yProps(index: any) {
        return {
            id: `scrollable-auto-tab-${index}`,
            "aria-controls": `scrollable-auto-tabpanel-${index}`,
        };
    }

    return (
        <Dialog
            onClose={onClose}
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth={true}
            maxWidth={"lg"}
        >
            <AppBar className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <Typography variant="h6" className={classes.title}>
                        {t("disposition.filter.displayFilter")}
                    </Typography>
                    <IconButton aria-label="close" onClick={onClose}>
                        <CloseIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <DialogActions style={{justifyContent: "flex-start"}}>
                <Tooltip title={t("disposition.controls.tooltip.applyFilter")}>
                    <Button
                        onClick={handleAcceptFilters}
                        color="primary"
                        size={"small"}
                        variant={"outlined"}
                    >
                        {t("disposition.controls.modal.buttonAcceptLabel")}
                    </Button>
                </Tooltip>
                <Tooltip
                    title={t("disposition.controls.tooltip.closeFilterModal")}
                >
                    <Button
                        variant={"outlined"}
                        onClick={onClose}
                        size={"small"}
                        color="secondary"
                    >
                        {t("disposition.controls.modal.buttonCancelLabel")}
                    </Button>
                </Tooltip>
            </DialogActions>
            <DialogContent style={{height: "60vh"}}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs example"
                >
                    <Tab
                        label={t("disposition.filter.general")}
                        icon={<SortIcon/>}
                        {...a11yProps(0)}
                    />
                    <Tab
                        label={t("disposition.filter.regionAndStation")}
                        icon={<EmojiTransportationIcon/>}
                        data-testid={"stationTab"}
                        {...a11yProps(1)}
                    />
                    {!isVehicleLog && <Tab
                        label={t("disposition.filter.carTypeAndGroup")}
                        icon={<DriveEtaIcon/>}
                        data-testid={"groupTab"}
                        {...a11yProps(2)}
                    />}
                    {!isVehicleLog && <Tab
                        label={t("disposition.filter.equipments")}
                        icon={<LocalGasStationIcon/>}
                        data-testid={"equipmentTab"}
                        {...a11yProps(3)}
                    />}
                </Tabs>
                <TabPanel value={value} index={0}>
                    <GeneralFilterTab
                        predefinedFilter={tmpShowOnlyGroupResOfStationFilter}
                        setTmpFilter={setTmpShowOnlyGroupResOfStationFilter}
                    />
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <StationFilterTab
                        predefinedFilter={tmpStationFilter}
                        setTmpFilter={setTmpStationFilter}
                        setTmpRegionFilter={setTmpRegionFilter}
                        viewData={viewData}
                    />
                </TabPanel>
                <TabPanel value={value} index={2}>
                    <GroupFilterTab
                        predefinedFilter={tmpGroupFilter}
                        setTmpFilter={setTmpGroupFilter}
                        setTmpCategoryFilter={setTmpCategoryFilter}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        groupsForFilter={groupsForFilter}
                        getGroupsForFilter={getGroupsForFilter}
                        viewData={viewData}
                    />
                </TabPanel>
                <TabPanel value={value} index={3}>
                    <EquipmentFilterTab
                        predefinedFilter={tmpEquipmentFilter}
                        setTmpFilter={setTmpEquipmentFilter}
                        viewData={viewData}
                    />
                </TabPanel>
            </DialogContent>
        </Dialog>
    );
};

function TabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`scrollable-auto-tabpanel-${index}`}
            aria-labelledby={`scrollable-auto-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    );
}

export default FilterModal;
